import React, { useState, useEffect, KeyboardEvent } from "react";
import {
  Box,
  Button,
  Dialog,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LockIcon from "@mui/icons-material/Lock";
import axios from "axios";
import { BASE_API_URL } from "../api/const";
import Register from "./Register";

// Тип пропсов для компонента Auth
interface AuthProps {
  onAuthSuccess: () => void; // функция без аргументов, вызывается после успешной авторизации
}

const Auth: React.FC<AuthProps> = ({ onAuthSuccess }) => {
  // Стейты с правильной типизацией
  const [login, setLogin] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [openRegister, setOpenRegister] = useState<boolean>(false);
  // codecName может быть строкой или undefined (пока не вычислен)
  const [codecName, setCodecName] = useState<string | undefined>(undefined);

  // Хук для вычисления домена из URL и установки codecName
  useEffect(() => {
    const url = window.location.href;
    const domain = url
      .replace(/^https?:\/\//, "")
      .split(".")[0]
      .split("/")[0];
    setCodecName(domain);
  }, []);

  // Хук для загрузки login из localStorage при монтировании
  useEffect(() => {
    const storedLogin = localStorage.getItem("mediateka.Login");
    if (storedLogin) {
      setLogin(storedLogin);
    }
  }, []);

  // Асинхронная функция логина с обработкой ошибок
  const handleLogin = async () => {
    try {
      const response = await axios.post(`${BASE_API_URL}/user/login`, {
        login,
        password,
      });

      if (response.data.success) {
        const { token } = response.data.data;
        localStorage.setItem("mediateka.Login", login);
        localStorage.setItem("mediateka.Token", token);
        onAuthSuccess();
      }
    } catch (error) {
      setLogin("");
      setPassword("");
      alert("Неправильный логин или пароль, см. в консоли код ошибки");
      console.error("Ошибка авторизации", error);
    }
  };

  // Открыть диалог регистрации
  const handleOpenRegister = () => setOpenRegister(true);

  // Закрыть диалог регистрации
  const handleCloseRegister = () => setOpenRegister(false);

  // Обработчик нажатия Enter в поле пароля
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        backgroundColor: "#F5F8FA",
        whiteSpace: "pre-line",
        textAlign: "center",
      }}
    >
      {/* Заголовок */}
      <Typography variant="h5" color="primary" sx={{ marginBottom: 2 }}>
        mediateka adminPanel
      </Typography>

      {/* Поля ввода */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
        width="300px"
      >
        <TextField
          label="Логин"
          variant="outlined"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AccountCircle />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Пароль"
          variant="outlined"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="success"
          onClick={handleLogin}
          fullWidth
        >
          Войти
        </Button>
        <Button variant="contained" onClick={handleOpenRegister}>
          Регистрация
        </Button>
      </Box>

      {/* Диалог регистрации */}
      <Dialog open={openRegister} onClose={handleCloseRegister}>
        <Register
          onClose={handleCloseRegister}
          onRegisterSuccess={onAuthSuccess}
        />
      </Dialog>
    </Box>
  );
};

export default Auth;
